import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './index.css';
import './ui/global.scss';

// 1. @tanstack query first code
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
// must do before use in other component
import LoadingScreen from './components/loadingscreen/LoadingScreen';
import PrivateRoute from './components/user/PrivateRoute';
import AutoReset from './hook/AutoReset';
import Auth_PasswordReset from './pages/Auth_PasswordReset';

import Dashboard from './pages/Dashboard';

import FlappyBird from './pages/FlappyBird';
import History from './pages/History';
import Login from './pages/Login';
import PageNotFound from './pages/PageNotFound';
import Profile from './pages/Profile';

import AppLayout from './ui/AppLayout';

import ChatPage from './pages/chat';
import Employees from './pages_tsx/Employees';
import FileManager from './pages_tsx/Filemanager';
import Payroll from './pages_tsx/Payroll';
import Services from './pages_tsx/Services';
import Setting from './pages_tsx/Setting';

// 2. Create a  @tanstack client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000, //  1sec to refetch data
    },
  },
});

const loadingTasks = [
  'Loading configuration...',
  'Initializing components...',
  'Loading user interface...',
  'Preparing dashboard components...',
  'Setting up authentication...',
  'Loading service modules...',
  'Initializing file manager...',
  'Setting up employee database...',
  'Finalizing setup...',
];

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState(loadingTasks[0]);
  const [taskIndex, setTaskIndex] = useState(0);

  useEffect(() => {
    const simulateLoading = () => {
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          const newProgress = prev + 4;

          // Update task based on progress
          const newTaskIndex = Math.min(
            Math.floor((newProgress / 100) * loadingTasks.length),
            loadingTasks.length - 1
          );

          if (newTaskIndex !== taskIndex) {
            setTaskIndex(newTaskIndex);
            setCurrentTask(loadingTasks[newTaskIndex]);
          }

          if (newProgress >= 100) {
            clearInterval(interval);
            setTimeout(() => setIsLoading(false), 500);
            return 100;
          }
          return newProgress;
        });
      }, 100);
    };

    simulateLoading();
  }, [taskIndex]);

  return (
    // 3. @tanstack - Provide the client to your App
    <QueryClientProvider client={queryClient}>
      <LoadingScreen isLoading={isLoading} progress={loadingProgress} currentTask={currentTask} />
      {!isLoading && (
        <BrowserRouter>
          {/* Everything within AppLayout */}
          <Routes>
            {/* Routes within the layout */}
            <Route element={<AppLayout />}>
              <Route index path="/" element={<Navigate replace to="/login" />} />

              {/* The following route is accessible by everyone */}
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="chat" element={<ChatPage />} />

              {/* Protecting routes using PrivateRoute */}
              <Route element={<PrivateRoute allowedRoles={['Manager', 'Owner']} />}>
                <Route path="employees" element={<Employees />} />
                <Route path="services" element={<Services />} />
                <Route path="filemanager" element={<FileManager />} />
              </Route>
              <Route element={<PrivateRoute allowedRoles={['Owner']} />}>
                <Route path="history" element={<History />} />
                <Route path="setting" element={<Setting />} />
                <Route path="payroll" element={<Payroll />} />
              </Route>
            </Route>
            {/* Pages not belonging to AppLayout */}
            <Route path="flappybird/:id" element={<FlappyBird />} />
            <Route path="profile/:id" element={<Profile />} />
            <Route path="login" element={<Login />} />
            <Route path="reset-password" element={<Auth_PasswordReset />} />
            <Route path="auth_passwordreset" element={<Auth_PasswordReset />} />

            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </BrowserRouter>
      )}
      <ReactQueryDevtools initialIsOpen={false} />
      <Toaster
        gutter={12}
        position="bottom-center"
        containerStyle={{
          margin: 8,
          bottom: '50px', // Adjust this value to move the toast up
        }}
        toastOptions={{
          success: { duration: 3000 },
          error: { duration: 5000 },
          style: {
            fontSize: '16px',
            fontWeight: 'normal',
            maxWidth: '450px',
            padding: '8px 16px',
            background: '#f3f3f3',
            color: '#262626',
          },
        }}
      />

      {/* Auto Reset taskManager and taskManager_history at timeReset */}
      <AutoReset />
      {/* 4. @tanstack - close tag*/}
    </QueryClientProvider>
  );
}

export default App;
